import PropTypes from 'prop-types';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import { styled } from '@mui/material/styles';
import { AppBar, Box, IconButton, Stack, Toolbar } from '@mui/material';
import useAuthStore from 'clientStore/useAuthStore';
import useBranchStore from 'clientStore/useBranchStore';
import useThemeStore from 'clientStore/useThemeStore';
import Logo from 'components/logo';
import MyTooltip from 'components/MyTooltip';
import { useNavigate } from 'react-router-dom';
import { bgBlur } from '../../../utils/cssStyles';
import Iconify from '../../../components/iconify';
import CompanyBranchHeader from './CompanyBranchHeader';

const NAV_WIDTH = 250;
const NAV_WIDTH_CLOSE = 80;
const HEADER_MOBILE = 64;
const HEADER_DESKTOP = 64;

const StyledRoot = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'openNav'
})(({ theme, openNav }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  color: theme.palette.text.primary,
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${(openNav ? NAV_WIDTH : NAV_WIDTH_CLOSE) + 1}px)`,
    marginLeft: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSE,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  px: 2,
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER_DESKTOP,
    px: 3
  }
}));

const squareIconSx = {
  width: 40,
  height: 40,
  borderRadius: 1.25,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  bgcolor: 'background.paper',
  '&:hover': {
    bgcolor: 'action.hover'
  }
};

Header.propTypes = {
  onOpenNav: PropTypes.func,
  openNav: PropTypes.bool
};

export default function Header({ onOpenNav, openNav = true }) {
  const navigate = useNavigate();
  const { setAccessToken } = useAuthStore();
  const { clearSelectedBranchId } = useBranchStore();
  const { mode, setTheme } = useThemeStore();

  const logout = () => {
    setAccessToken(null);
    clearSelectedBranchId();
    navigate('/', { replace: true });
  };

  return (
    <StyledRoot openNav={openNav}>
      <StyledToolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box sx={{ display: { xs: 'block', lg: 'none' }, maxWidth: 150 }}>
            <Logo />
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 'auto', flexShrink: 0 }}>
          <CompanyBranchHeader />

          <IconButton
            aria-label="toggle-theme"
            onClick={() => setTheme(mode === 'dark' ? 'light' : 'dark')}
            sx={{ ...squareIconSx, display: { xs: 'none', sm: 'inline-flex' } }}>
            {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </IconButton>

          <MyTooltip title="Logout" placement="bottom">
            <IconButton aria-label="logout" onClick={logout} sx={squareIconSx}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </MyTooltip>

          <IconButton
            onClick={onOpenNav}
            aria-label="open-menu"
            sx={{
              ...squareIconSx,
              display: { lg: 'none' }
            }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButton>
        </Stack>
      </StyledToolbar>
    </StyledRoot>
  );
}
